/**
 * NWF-62: states around Klaviyo embeds (`snippets/nwf-klaviyo-embed.liquid`).
 *
 * The happy path needs no JavaScript beyond the loader: Klaviyo injects the form
 * into `.klaviyo-form-{ID}` and CSS (`:has()`) drops the skeleton. This file
 * covers what happens around it:
 *
 * 1. No marketing consent (NWF-257) — the embed shows the consent overlay from
 *    nwf-video-consent.js. "Cookies akzeptieren" or the banner grants consent,
 *    nwf-klaviyo-consent.js then loads klaviyo.js and the form renders.
 * 2. Consent given, but the form does not render (ad blocker, Klaviyo down) —
 *    a hint replaces the skeleton. It disappears again if the form shows up late.
 * 3. Theme Editor — `shopify:section:load` replaces the section with a fresh,
 *    empty embed after Klaviyo's initial scan. A clone of the target gives
 *    Klaviyo's DOM watcher a new node to pick up.
 *
 * The snippet can request this file twice on one page (newsletter page and
 * footer); the init guard keeps one set of observers.
 */
(() => {
    const NUDGE_DELAY = 2500;
    // Klaviyo usually renders within about a second after loading; a late form still replaces the hint.
    const FAIL_DELAY = 3000;

    if (window.nwfKlaviyoEmbedReady) return;
    window.nwfKlaviyoEmbedReady = true;

    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    const watched = new WeakSet();

    const targetOf = (embed) => embed.querySelector('[data-nwf-klaviyo-target]');
    const isRendered = (embed) => (targetOf(embed)?.childElementCount || 0) > 0;

    /** Klaviyo skips nodes it has already processed, so the same element would be a no-op. */
    const nudge = (embed) => {
        const target = targetOf(embed);
        if (target) target.replaceWith(target.cloneNode(false));
    };

    const setHint = (embed, visible) => {
        const hint = embed.querySelector('[data-nwf-klaviyo-hint]');
        if (!hint) return;
        const variant = inEditor ? 'editor' : 'blocked';
        hint.querySelectorAll('[data-nwf-klaviyo-hint-for]').forEach((message) => {
            message.hidden = !visible || message.dataset.nwfKlaviyoHintFor !== variant;
        });
        hint.hidden = !visible;
        if (visible) embed.dataset.nwfKlaviyoState = 'failed';
        else delete embed.dataset.nwfKlaviyoState;
    };

    const expect = (embed) => {
        if (isRendered(embed)) return;

        const timers = [];
        const observer = new MutationObserver(() => {
            if (!isRendered(embed)) return;
            observer.disconnect();
            timers.forEach(window.clearTimeout);
            setHint(embed, false);
        });
        observer.observe(embed, { childList: true, subtree: true });

        if (inEditor) timers.push(window.setTimeout(() => isRendered(embed) || nudge(embed), NUDGE_DELAY));
        timers.push(window.setTimeout(() => isRendered(embed) || setHint(embed, true), FAIL_DELAY));
    };

    const watch = (embed) => {
        if (watched.has(embed)) return;
        watched.add(embed);

        const consent = window.NwfVideoConsent;
        if (inEditor || !consent) {
            expect(embed);
            return;
        }

        embed.dataset.nwfKlaviyoState = 'gated';
        consent.gate(
            embed,
            'klaviyo',
            () => {
                delete embed.dataset.nwfKlaviyoState;
                window.NwfKlaviyo?.load();
                expect(embed);
            },
            { image: false },
        );
    };

    const watchAll = (root) => {
        root.querySelectorAll('[data-nwf-klaviyo-embed]').forEach(watch);
    };

    watchAll(document);

    document.addEventListener('shopify:section:load', (event) => {
        watchAll(event.target);
    });
})();
